import React, { useState } from 'react';

interface ProfileAvatarProps {
  imageUrl?: string | null;
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * ProfileAvatar component displays user profile images with fallback to initials
 *
 * Features:
 * - Three size variants: sm (40px), md (48px), lg (64px)
 * - Initials-based fallback with branded color scheme
 * - Automatic error handling with fallback
 * - Lazy loading for performance
 * - Rounded corners for visual consistency
 */
const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  imageUrl,
  name,
  size = 'md',
  className = ''
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Size configurations
  const sizeConfig = {
    sm: { dimension: 40, fontSize: 16 },
    md: { dimension: 48, fontSize: 18 },
    lg: { dimension: 64, fontSize: 24 }
  };

  const config = sizeConfig[size];

  // Generate initials from name
  const getInitials = (fullName: string): string => {
    if (!fullName || fullName.trim() === '') {
      return '?';
    }

    const parts = fullName.trim().split(/\s+/);

    if (parts.length === 1) {
      // Single name: use first two characters
      return parts[0].substring(0, 2).toUpperCase();
    }

    // Multiple names: use first letter of first and last name
    const firstInitial = parts[0].charAt(0);
    const lastInitial = parts[parts.length - 1].charAt(0);
    return (firstInitial + lastInitial).toUpperCase();
  };

  const initials = getInitials(name);

  // Handle image load error
  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  // Handle image load success
  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  // Determine if we should show the image or fallback
  const showImage = imageUrl && !imageError;

  const avatarStyle: React.CSSProperties = {
    width: `${config.dimension}px`,
    height: `${config.dimension}px`,
    borderRadius: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    position: 'relative',
    backgroundColor: showImage ? '#E6EDF2' : '#1A3A5D', // Light grey-blue or Deep Blue
    color: '#FFFFFF',
    fontSize: `${config.fontSize}px`,
    fontWeight: 600,
    userSelect: 'none'
  };

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: imageLoaded ? 'block' : 'none'
  };

  const fallbackStyle: React.CSSProperties = {
    display: showImage && imageLoaded ? 'none' : 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%'
  };

  return (
    <div
      className={`profile-avatar ${className}`}
      style={avatarStyle}
      title={name}
    >
      {showImage && (
        <img
          src={imageUrl}
          alt={`${name}'s profile`}
          style={imageStyle}
          onError={handleImageError}
          onLoad={handleImageLoad}
          loading="lazy"
        />
      )}
      <div style={fallbackStyle}>
        {initials}
      </div>
    </div>
  );
};

export default ProfileAvatar;
